import { memo, useCallback } from 'react';
import { Badge, Input } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { hasEmailSeparator } from '../../utils/parseEmails.js';
import ExistingMemberRow from './ExistingMemberRow';
import InvalidEmailRow from './InvalidEmailRow';

/**
 * Emails of the People step: the typing field, the chips of the valid addresses, the
 * correct-or-remove box for invalid entries and the "Already in the workspace" list.
 * A comma confirms each address as you type, Enter adds what is left, a paste adds the list.
 *
 * @param {object} props
 * @param {string[]} props.emails Valid, new addresses (chips).
 * @param {string} props.draft Text typed and not yet confirmed.
 * @param {(value: string) => void} props.onDraftChange
 * @param {string[]} props.invalid Entries that are not an address.
 * @param {Array<{ email: string, name: string, status: string }>} props.existing Already members.
 * @param {(raw: string) => void} props.onAdd
 * @param {(email: string) => void} props.onRemove
 * @param {(value: string, corrected: string) => void} props.onCorrectInvalid
 * @param {(value: string) => void} props.onRemoveInvalid
 * @param {(email: string) => void} props.onRemoveExisting
 */
function EmailsField({
  emails,
  draft,
  onDraftChange,
  invalid,
  existing,
  onAdd,
  onRemove,
  onCorrectInvalid,
  onRemoveInvalid,
  onRemoveExisting,
}) {
  const { t } = useTranslation('people-access');

  const handleChange = useCallback(
    (event) => {
      const value = event.target.value;
      const lastComma = value.lastIndexOf(',');
      if (lastComma === -1) {
        onDraftChange(value);
        return;
      }
      onAdd(value.slice(0, lastComma));
      onDraftChange(value.slice(lastComma + 1));
    },
    [onAdd, onDraftChange],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (!draft.trim()) return;
      onAdd(draft);
      onDraftChange('');
    },
    [draft, onAdd, onDraftChange],
  );

  const handlePaste = useCallback(
    (event) => {
      const text = event.clipboardData.getData('text');
      if (!hasEmailSeparator(text)) return;
      event.preventDefault();
      onAdd(text);
    },
    [onAdd],
  );

  return (
    <div className="flex flex-col gap-3">
      <Input
        label={t('invite.people.emailsLabel')}
        helperText={t('invite.people.emailsHelper')}
        placeholder={t('invite.people.emailsPlaceholder')}
        value={draft}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        autoComplete="off"
      />

      {emails.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {emails.map((email) => (
            <li key={email}>
              <Badge
                variant="informative"
                id={email}
                closable
                onClose={onRemove}
                closeButtonAriaLabel={t('invite.people.removeEmail', { email })}
              >
                {email}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {(invalid.length > 0 || existing.length > 0) && (
        <div className="flex flex-col gap-4 rounded-lg border border-edge bg-surface-muted p-4">
          {invalid.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="m-0 text-sm font-semibold text-content">
                {t('invite.people.invalidTitle', { count: invalid.length })}
              </h3>
              <p className="m-0 text-xs text-content-muted">{t('invite.people.invalidHelper')}</p>
              {invalid.map((value) => (
                <InvalidEmailRow
                  key={value}
                  value={value}
                  onCorrect={onCorrectInvalid}
                  onRemove={onRemoveInvalid}
                />
              ))}
            </section>
          )}
          {existing.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="m-0 text-sm font-semibold text-content">
                {t('invite.people.existingTitle')}
              </h3>
              {existing.map((member) => (
                <ExistingMemberRow key={member.email} member={member} onRemove={onRemoveExisting} />
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(EmailsField);
